// Socket.IO singleton wired to the same JWT used by the REST API. The hook
// returns the live socket and reconnects automatically when the access token
// changes (post-login or post-refresh).

import { io, Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
} from '@marnic/shared/events';
import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/auth';
import { getDeviceId } from './device-id';

const baseUrl: string = import.meta.env.VITE_WS_URL || window.location.origin;

let socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;
let socketToken: string | null = null;

export function getSocket(): Socket<ServerToClientEvents, ClientToServerEvents> | null {
  return socket;
}

export function ensureSocket(token: string): Socket<ServerToClientEvents, ClientToServerEvents> {
  if (socket && socketToken === token) return socket;
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  socketToken = token;
  socket = io(baseUrl, {
    path: '/socket.io',
    transports: ['websocket'],
    auth: { token, deviceId: getDeviceId() },
    reconnection: true,
    reconnectionDelay: 1500,
    reconnectionDelayMax: 8000,
  });
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
    socketToken = null;
  }
}

export function useSocket(): Socket<ServerToClientEvents, ClientToServerEvents> | null {
  const token = useAuthStore((s) => s.accessToken);
  const [, force] = useState(0);

  useEffect(() => {
    if (!token) {
      disconnectSocket();
      force((n) => n + 1);
      return;
    }
    ensureSocket(token);
    force((n) => n + 1);
    return () => {
      // Keep socket alive across component remounts; only fully tear down at logout.
    };
  }, [token]);

  return socket;
}
